import { ContentType, Audience, ClinicalArea, Topic, ResourceMetadata } from '../types/metadata';
import {
  UserGroupIcon,
  UserIcon,
  BeakerIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
  BookOpenIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ShoppingCartIcon,
  HeartIcon,
  QuestionMarkCircleIcon,
  FolderIcon,
  CalendarDaysIcon,
  NewspaperIcon,
  GlobeAltIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';

type HeroIcon = ComponentType<SVGProps<SVGSVGElement>>;

/**
 * Content type display names and colors
 */
export const contentTypeConfig: Record<
  ContentType,
  { label: string; color: string; icon: HeroIcon }
> = {
  [ContentType.Toolkit_Guideline]: {
    label: 'Toolkit / Guideline',
    color: 'bg-content-toolkit',
    icon: FolderIcon,
  },
  [ContentType.Template_Form]: {
    label: 'Template / Form',
    color: 'bg-content-template',
    icon: DocumentTextIcon,
  },
  [ContentType.Educational_Training]: {
    label: 'Educational / Training',
    color: 'bg-content-educational',
    icon: AcademicCapIcon,
  },
  [ContentType.Audit_Report]: {
    label: 'Audit / Report',
    color: 'bg-content-audit',
    icon: ChartBarIcon,
  },
  [ContentType.Event_Webinar]: {
    label: 'Event / Webinar',
    color: 'bg-content-event',
    icon: CalendarDaysIcon,
  },
  [ContentType.Publication_Article]: {
    label: 'Publication / Article',
    color: 'bg-content-publication',
    icon: NewspaperIcon,
  },
  [ContentType.Landing_Portal]: {
    label: 'Landing / Portal',
    color: 'bg-content-landing',
    icon: GlobeAltIcon,
  },
  [ContentType.Reference_External]: {
    label: 'Reference / External',
    color: 'bg-content-reference',
    icon: LinkIcon,
  },
};

/**
 * Audience display names and icons
 */
export const audienceConfig: Record<Audience, { label: string; icon: typeof UserIcon }> = {
  [Audience.Nurses]: { label: 'Nurses', icon: UserGroupIcon },
  [Audience.Physicians]: { label: 'Physicians', icon: UserIcon },
  [Audience.Transfusionists]: { label: 'Transfusionists', icon: BeakerIcon },
  [Audience.Laboratory]: { label: 'Technologists', icon: BeakerIcon },
  [Audience.Administrators]: { label: 'Administrators', icon: BuildingOfficeIcon },
  [Audience.QualitySafety]: { label: 'Quality & Safety', icon: ShieldCheckIcon },
  [Audience.Educators]: { label: 'Educators', icon: AcademicCapIcon },
  [Audience.StudentsTrainees]: { label: 'Students & Trainees', icon: BookOpenIcon },
  [Audience.DataAnalytics]: { label: 'Data & Analytics', icon: ChartBarIcon },
  [Audience.PolicyRegulators]: { label: 'Policy & Regulators', icon: DocumentTextIcon },
  [Audience.ProcurementInventory]: { label: 'Procurement & Inventory', icon: ShoppingCartIcon },
  [Audience.PatientsCaregivers]: { label: 'Patients & Caregivers', icon: HeartIcon },
  [Audience.Other]: { label: 'Other', icon: QuestionMarkCircleIcon },
  [Audience.Unknown]: { label: 'Unknown', icon: QuestionMarkCircleIcon },
};

/**
 * Clinical area display names
 */
export const clinicalAreaLabels: Record<ClinicalArea, string> = {
  [ClinicalArea.TransfusionMedicine]: 'Transfusion Medicine',
  [ClinicalArea.Hematology]: 'Hematology',
  [ClinicalArea.Neurology]: 'Neurology',
  [ClinicalArea.Rheumatology]: 'Rheumatology',
  [ClinicalArea.Anesthesiology]: 'Anesthesiology',
  [ClinicalArea.Obstetrics]: 'Obstetrics',
  [ClinicalArea.PediatricsNeonatology]: 'Pediatrics / Neonatology',
  [ClinicalArea.Emergency]: 'Emergency',
  [ClinicalArea.ICUCriticalCare]: 'ICU / Critical Care',
  [ClinicalArea.SurgeryPerioperative]: 'Surgery / Perioperative',
  [ClinicalArea.Oncology]: 'Oncology',
  [ClinicalArea.Cardiology]: 'Cardiology',
  [ClinicalArea.InternalMedicine]: 'Internal Medicine',
  [ClinicalArea.HomeInfusion]: 'Home Infusion',
  [ClinicalArea.PrimaryCare]: 'Primary Care',
  [ClinicalArea.Dermatology]: 'Dermatology',
  [ClinicalArea.Immunology]: 'Immunology',
  [ClinicalArea.InfectiousDiseases]: 'Infectious Diseases',
  [ClinicalArea.Transplant]: 'Transplant',
  [ClinicalArea.Other]: 'Other',
  [ClinicalArea.Unknown]: 'Unknown',
};

/**
 * Topic display names. Acronyms stay intact.
 */
export const topicLabels: Record<Topic, string> = {
  [Topic.IVIG]: 'IVIG',
  [Topic.SCIG]: 'SCIG',
  [Topic.IGOrderingOntario]: 'IG Ordering Ontario',
  [Topic.Utilization]: 'Utilization',
  [Topic.MHP]: 'MHP',
  [Topic.Shortage]: 'Shortage',
  [Topic.Redistribution]: 'Redistribution',
  [Topic.InventoryManagement]: 'Inventory Management',
  [Topic.RBC]: 'RBC',
  [Topic.Plasma]: 'Plasma',
  [Topic.ABPlasma]: 'AB Plasma',
  [Topic.Platelets]: 'Platelets',
  [Topic.Cryoprecipitate]: 'Cryoprecipitate',
  [Topic.Albumin]: 'Albumin',
  [Topic.C1EsteraseInhibitors]: 'C1 Esterase Inhibitors',
  [Topic.ImmuneSerumGlobulin]: 'Immune Serum Globulin',
  [Topic.Prenatal]: 'Prenatal',
  [Topic.Postnatal]: 'Postnatal',
  [Topic.Neonatal]: 'Neonatal',
  [Topic.FetalGenotyping]: 'Fetal Genotyping',
  [Topic.Rounds]: 'Rounds',
  [Topic.BootCamp]: 'Boot Camp',
  [Topic.Symposium]: 'Symposium',
  [Topic.Webinar]: 'Webinar',
  [Topic.GHEST]: 'GHEST',
  [Topic.eLearning]: 'eLearning',
  [Topic.TrainingEducation]: 'Training Education',
  [Topic.Guidelines]: 'Guidelines',
  [Topic.Procedures]: 'Procedures',
  [Topic.Templates]: 'Templates',
  [Topic.Forms]: 'Forms',
  [Topic.Checklists]: 'Checklists',
  [Topic.ReferenceManual]: 'Reference Manual',
  [Topic.Policy]: 'Policy',
  [Topic.Regulation]: 'Regulation',
  [Topic.BloodSafety]: 'Blood Safety',
  [Topic.PublicHealth]: 'Public Health',
  [Topic.Research]: 'Research',
  [Topic.Quality]: 'Quality',
  [Topic.Education]: 'Education',
  [Topic.Safety]: 'Safety',
  [Topic.Clinical]: 'Clinical',
  [Topic.Audit]: 'Audit',
  [Topic.ProvincialAuditReport]: 'Provincial Audit Report',
  [Topic.ORBCoNAnnualReport]: 'ORBCoN Annual Report',
  [Topic.ValidationVerification]: 'Validation Verification',
  [Topic.ProspectiveScreening]: 'Prospective Screening',
  [Topic.DataDashboard]: 'Data Dashboard',
  [Topic.ORBCoNReport]: 'ORBCoN Report',
  [Topic.TransfusionistsTalk]: 'Transfusionists Talk',
  [Topic.NewsAnnouncement]: 'News Announcement',
  [Topic.PatientFactSheets]: 'Patient Fact Sheets',
  [Topic.ExternalLink]: 'External Link',
  [Topic.Other]: 'Other',
  [Topic.Unknown]: 'Unknown',
};

export function formatTopicLabel(topic: Topic): string {
  return topicLabels[topic];
}

/**
 * Get audience color class
 */
export function getAudienceColor(audience: Audience): string {
  const colors: Record<Audience, string> = {
    [Audience.Nurses]: 'bg-pink-100 text-pink-700',
    [Audience.Physicians]: 'bg-blue-100 text-blue-700',
    [Audience.Transfusionists]: 'bg-purple-100 text-purple-700',
    [Audience.Laboratory]: 'bg-green-100 text-green-700',
    [Audience.Administrators]: 'bg-indigo-100 text-indigo-700',
    [Audience.QualitySafety]: 'bg-amber-100 text-amber-700',
    [Audience.Educators]: 'bg-cyan-100 text-cyan-700',
    [Audience.StudentsTrainees]: 'bg-teal-100 text-teal-700',
    [Audience.DataAnalytics]: 'bg-violet-100 text-violet-700',
    [Audience.PolicyRegulators]: 'bg-slate-100 text-slate-700',
    [Audience.ProcurementInventory]: 'bg-orange-100 text-orange-700',
    [Audience.PatientsCaregivers]: 'bg-rose-100 text-rose-700',
    [Audience.Other]: 'bg-gray-100 text-gray-700',
    [Audience.Unknown]: 'bg-gray-100 text-gray-700',
  };
  return colors[audience] || colors[Audience.Other];
}

export type CardMetadataChip =
  | { kind: 'audience'; value: Audience }
  | { kind: 'clinicalArea'; value: ClinicalArea }
  | { kind: 'topic'; value: Topic };

export function resourceFacetChips(metadata: ResourceMetadata): CardMetadataChip[] {
  return [
    ...(metadata.audiences ?? []).map((value) => ({ kind: 'audience' as const, value })),
    ...(metadata.clinicalAreas ?? []).map((value) => ({ kind: 'clinicalArea' as const, value })),
    ...(metadata.topics ?? []).map((value) => ({ kind: 'topic' as const, value })),
  ];
}

